import type { MeteringBox } from '@/types';
import type { VideoRotation } from '@/lib/videoRotation';

/**
 * Light-metering box geometry. The phone meters on the frame as it is stored, so a box is kept in
 * RAW frame fractions (0..1, origin top-left, before display rotation). The editor shows the
 * picture upright, turned `rotation` degrees clockwise like the session player, and converts here.
 */

/** Smallest box side the server accepts, as a fraction of the frame. */
export const METERING_BOX_MIN_SIDE = 0.02;

/** A point as fractions 0..1 of a picture, origin top-left, x right, y down. */
export interface FractionPoint {
  x: number;
  y: number;
}

/** A point on the upright picture to the same point on the stored frame. */
export const displayToRaw = ({ x, y }: FractionPoint, rotation: VideoRotation): FractionPoint => {
  switch (rotation) {
    case 90:
      return { x: y, y: 1 - x };
    case 180:
      return { x: 1 - x, y: 1 - y };
    case 270:
      return { x: 1 - y, y: x };
    default:
      return { x, y };
  }
};

/** The inverse of {@link displayToRaw}. */
export const rawToDisplay = ({ x, y }: FractionPoint, rotation: VideoRotation): FractionPoint => {
  switch (rotation) {
    case 90:
      return { x: 1 - y, y: x };
    case 180:
      return { x: 1 - x, y: 1 - y };
    case 270:
      return { x: y, y: 1 - x };
    default:
      return { x, y };
  }
};

/** The box spanned by two opposite corners, given in either order. */
export const boxBetween = (a: FractionPoint, b: FractionPoint): MeteringBox => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  w: Math.abs(a.x - b.x),
  h: Math.abs(a.y - b.y),
});

/** Where a stored-frame box shows on the upright picture. */
export const rawBoxToDisplay = (box: MeteringBox, rotation: VideoRotation): MeteringBox =>
  boxBetween(
    rawToDisplay({ x: box.x, y: box.y }, rotation),
    rawToDisplay({ x: box.x + box.w, y: box.y + box.h }, rotation),
  );

/** A box drawn on the upright picture, as a stored-frame box. Not clamped. */
export const displayBoxToRaw = (box: MeteringBox, rotation: VideoRotation): MeteringBox =>
  boxBetween(
    displayToRaw({ x: box.x, y: box.y }, rotation),
    displayToRaw({ x: box.x + box.w, y: box.y + box.h }, rotation),
  );

const clampUnit = (v: number): number => (Number.isNaN(v) ? 0 : Math.min(1, Math.max(0, v)));

const round4 = (v: number): number => Math.round(v * 10_000) / 10_000;

// The side is settled first and the start clamped to `1 - side` after, so neither the rounding nor
// the growth to the minimum can push `start + side` past 1 or the side under the minimum: the
// server refuses both.
const clampAxis = (start: number, size: number): [number, number] => {
  const lo = clampUnit(Math.min(start, start + size));
  const hi = clampUnit(Math.max(start, start + size));
  const side = round4(Math.max(METERING_BOX_MIN_SIDE, hi - lo));
  const at = Math.min(Math.max(0, round4((lo + hi) / 2 - side / 2)), round4(1 - side));
  return [at, side];
};

/**
 * A box the server accepts: inside the frame, each side at least {@link METERING_BOX_MIN_SIDE}
 * (grown about its centre when smaller), rounded to 4 decimals.
 */
export const clampMeteringBox = (box: MeteringBox): MeteringBox => {
  const [x, w] = clampAxis(box.x, box.w);
  const [y, h] = clampAxis(box.y, box.h);
  return { x, y, w, h };
};

/**
 * The stored-frame box a drag between two points on the upright picture draws. Null when both
 * sides are under the minimum: that is a tap, and a tap must not replace the box already there.
 */
export const boxFromDrag = (
  from: FractionPoint,
  to: FractionPoint,
  rotation: VideoRotation,
): MeteringBox | null => {
  const drawn = boxBetween(from, to);
  if (drawn.w < METERING_BOX_MIN_SIDE && drawn.h < METERING_BOX_MIN_SIDE) return null;
  return clampMeteringBox(displayBoxToRaw(drawn, rotation));
};

export const sameMeteringBox = (a: MeteringBox | null, b: MeteringBox | null): boolean =>
  a === b || (a !== null && b !== null && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h);
